import { isEmpty } from 'lodash'
import { mutationNames } from '@/store/storeKey'
import { HashCodeFromInt, HexCodeFromColor } from '@/modules/helper/index'
import api from '@/modules/api'

const state = {
  subscriptionsDesignSettings: {},
  subscriptionsStartPages: {},
  subscriptionsModules: []
}

const getters = {
  subscriptionsDesignSettings: state => {
    return isEmpty(state.subscriptionsDesignSettings) ? {} : {
      ...state.subscriptionsDesignSettings,
      AppThemeColor: HashCodeFromInt(state.subscriptionsDesignSettings.AppThemeColor),
      AppThemeFontColor: HashCodeFromInt(state.subscriptionsDesignSettings.AppThemeFontColor),
      AppThemeSecondaryFontColor: HashCodeFromInt(state.subscriptionsDesignSettings.AppThemeSecondaryFontColor),
      BackgroundColor: HashCodeFromInt(state.subscriptionsDesignSettings.BackgroundColor),
      BackgroundColorElements: HashCodeFromInt(state.subscriptionsDesignSettings.BackgroundColorElements),
      BackgroundFontColor: HashCodeFromInt(state.subscriptionsDesignSettings.BackgroundFontColor),
      TitleBarFontColor: HashCodeFromInt(state.subscriptionsDesignSettings.TitleBarFontColor)
    }
  },
  subscriptionsStartPages: state => {
    return isEmpty(state.subscriptionsStartPages) ? {} : {
      ...state.subscriptionsStartPages,
      MenuItemFontColor: HashCodeFromInt(state.subscriptionsStartPages.MenuItemFontColor),
      TitleBarFontColor: HashCodeFromInt(state.subscriptionsStartPages.TitleBarFontColor),
      MenuItemOverflowBackgroundColor: HashCodeFromInt(state.subscriptionsStartPages.MenuItemOverflowBackgroundColor)
    }
  },
  subscriptionsModules: (state, getters) => {
    return state.subscriptionsModules.map(module => {
      const sysModule = getters.systemSubscriptionModules.find(systemModule => systemModule.Id === module.Module_Id)
      return {
        ...module,
        MenuIconColor: HashCodeFromInt(module.MenuIconColor),
        ModuleIconNormal: sysModule && sysModule.ModuleIconNormal,
        ModuleIconInverted: sysModule && sysModule.ModuleIconInverted
      }
    })
  }
}

const mutations = {
  [mutationNames.SET_DESIGN_SETTINGS] (state, subscriptionsDesignSettings) {
    state.subscriptionsDesignSettings = subscriptionsDesignSettings
  },
  [mutationNames.UPDATE_DESIGN_SETTINGS] (state, obj) {
    state.subscriptionsDesignSettings = { ...state.subscriptionsDesignSettings, ...obj }
  },
  [mutationNames.SET_START_PAGE] (state, subscriptionsStartPages) {
    state.subscriptionsStartPages = subscriptionsStartPages
  },
  [mutationNames.UPDATE_START_PAGE_SETTINGS] (state, obj) {
    state.subscriptionsStartPages = { ...state.subscriptionsStartPages, ...obj }
  },
  [mutationNames.SET_SUBSCRIPTIONS_MODULES] (state, subscriptionsModules) {
    state.subscriptionsModules = subscriptionsModules
  },
  [mutationNames.UPDATE_SUBSCRIPTIONS_MODULE] (state, { Id, payload }) {
    const index = state.subscriptionsModules.findIndex(module => module.Id === Id)
    const updatedModule = {
      ...state.subscriptionsModules[index],
      ...payload
    }
    state.subscriptionsModules = [
      ...state.subscriptionsModules.slice(0, index),
      updatedModule,
      ...state.subscriptionsModules.slice(index + 1)
    ]
  }
}

const actions = {
  async populateSubscriptionsDesignSettings ({ commit, rootGetters }) {
    // if (isEmpty(rootGetters.subscriptionsDesignSettings)) {
    const response = await api.subscription.getDesignSettings(rootGetters.activeSubscriptionId)
    commit(mutationNames.SET_DESIGN_SETTINGS, response)
    // }
  },
  updateSubscriptionsDesignSettings ({ commit }, obj) {
    if (obj) {
      const props = Object.keys(obj)
      const colorProps = [
        'AppThemeColor',
        'AppThemeFontColor',
        'AppThemeSecondaryFontColor',
        'BackgroundColor',
        'BackgroundColorElements',
        'BackgroundFontColor',
        'TitleBarFontColor'
      ]
      props.forEach(prop => {
        if (colorProps.includes(prop)) {
          obj[prop] = HexCodeFromColor(obj[prop])
        }
      })
      commit(mutationNames.UPDATE_DESIGN_SETTINGS, obj)
    }
  },
  saveSubscriptionDesignSettings ({ state }) {
    return api.subscription.saveDesignSettings(state.subscriptionsDesignSettings)
  },
  async populateSubscriptionsStartPages ({ commit, rootGetters }) {
    // if (isEmpty(rootGetters.subscriptionsStartPages)) {
    const response = await api.subscription.getStartPage(rootGetters.activeSubscriptionId)
    commit(mutationNames.SET_START_PAGE, response)
    // }
  },
  updateSubscriptionsStartPages ({ commit }, obj) {
    if (obj) {
      const props = Object.keys(obj)
      const colorProps = [
        'MenuItemFontColor',
        'TitleBarFontColor',
        'MenuItemOverflowBackgroundColor'
      ]
      props.forEach(prop => {
        if (colorProps.includes(prop)) {
          obj[prop] = HexCodeFromColor(obj[prop])
        }
      })
      commit(mutationNames.UPDATE_START_PAGE_SETTINGS, obj)
    }
  },
  saveSubscriptionStartPage ({ state }) {
    return api.subscription.saveStartPage(state.subscriptionsStartPages)
  },
  async updateStartPageBackgroundImage ({ commit, getters }, uploadResponse) {
    await api.subscription.patchStartPage(getters.subscriptionsStartPages.Id, {
      BackgroundImage: uploadResponse ? uploadResponse.ImageName : null
    })
    commit(mutationNames.UPDATE_START_PAGE_SETTINGS, {
      // 'BackgroundImage_Id': uploadResponse ? uploadResponse.Id : null,
      'BackgroundImage': uploadResponse ? uploadResponse.ImageName : null
    })
  },
  async updateStartPageTitleLogo ({ commit, getters }, uploadResponse) {
    await api.subscription.patchStartPage(getters.subscriptionsStartPages.Id, {
      TitleBarLogo: uploadResponse ? uploadResponse.ImageName : null
    })
    commit(mutationNames.UPDATE_START_PAGE_SETTINGS, {
      // 'TitleBarLogo_Id': uploadResponse ? uploadResponse.Id : null,
      'TitleBarLogo': uploadResponse ? uploadResponse.ImageName : null
    })
  },
  async populateSubscriptionsModules ({ commit, rootGetters }) {
    // if (isEmpty(rootGetters.subscriptionsModules)) {
    const response = await api.subscription.getModules(rootGetters.activeSubscriptionId)
    commit(mutationNames.SET_SUBSCRIPTIONS_MODULES, response)
    // }
  },
  updateSubscriptionsModule ({ commit }, { Id, payload }) {
    if (payload) {
      const colorProps = [
        'MenuIconColor'
      ]
      const props = Object.keys(payload)
      props.forEach(prop => {
        if (colorProps.includes(prop)) {
          payload[prop] = HexCodeFromColor(payload[prop])
        }
      })
      commit(mutationNames.UPDATE_SUBSCRIPTIONS_MODULE, { Id, payload })
    }
  },
  setSubscriptionsModules ({ commit }, modules) {
    const updatedModules = modules ? modules.map((module, index) => {
      return {
        ...module,
        OrderId: index + 1,
        MenuIconColor: HexCodeFromColor(module.MenuIconColor)
      }
    }) : []
    commit(mutationNames.SET_SUBSCRIPTIONS_MODULES, updatedModules)
  },
  saveSubscriptionModules ({ state }) {
    return api.subscription.saveModules(state.subscriptionsModules)
  },
  saveAppDesignSettings ({ state, getters }) {
    // console.log('getters', getters)
    // const {
    //   subscriptionsDesignSettings,
    //   subscriptionsStartPages,
    //   subscriptionsModules
    // } = getters
    // delete subscriptionsDesignSettings.$id
    // delete subscriptionsStartPages.$id
    // subscriptionsModules.forEach((module) => {
    //   delete module.$id
    // })
    // console.log('getters', {
    //   subscriptionsDesignSettings,
    //   subscriptionsStartPages,
    //   subscriptionsModules
    // })
    // let payload = {
    //   subscriptionsModules,
    //   DesignSettings: subscriptionsDesignSettings,
    //   StartPages: subscriptionsStartPages
    // }
    let {
      subscriptionsDesignSettings,
      subscriptionsStartPages,
      subscriptionsModules
    } = state
    delete subscriptionsDesignSettings.$id
    delete subscriptionsStartPages.$id
    subscriptionsModules.forEach((module) => {
      delete module.$id
      return { ...module }
    })
    let payload = {
      subscriptionsModules: subscriptionsModules,
      DesignSettings: { ...subscriptionsDesignSettings },
      StartPages: { ...subscriptionsStartPages }
    }
    return api.subscription.saveAppDesignSettings(payload)
  },
  async saveSubscriptionRelatedSettings ({ dispatch }) {
    /* await Promise.all([
      dispatch('saveSubscriptionDesignSettings'),
      dispatch('saveSubscriptionStartPage')
    ]) */
    // return dispatch('saveSubscriptionModules')
    return dispatch('saveAppDesignSettings')
  },
  async captureHomeScreen ({ rootGetters }) {
    const payload = {
      url: `${rootGetters.domainSettings.AppsFTPURL}${rootGetters.subscription.Subscription_GUID}${rootGetters.domainSettings.WebAppPath}?screencapture=true`,
      Subscription_Id: rootGetters.activeSubscriptionId,
      Subscription_GUID: rootGetters.subscription.Subscription_GUID.toUpperCase()
    }
    let result = await api.screenShot.capture(payload)
    return result
  },
  addSubscriptionsModule ({ commit }, payload) {
    return api.subscription.addSubscriptionsModule(payload)
  },
  deleteSubscriptionsModule ({ commit }, Id) {
    return api.subscription.deleteSubscriptionsModule(Id)
  },
  setModulePassword ({ commit }, payload) {
    return api.subscription.setModulePassword(payload)
  },
  async UpdateMenuLabel ({ state }, payload) {
    return api.subscription.updateMenuLabel(payload)
  },
  async UpdateHideModule ({ state }, payload) {
    return api.subscription.updateHideModule(payload)
  }
}
export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/subscriptionRelated.js